/**
 * Tache 1291 — Sentinelle « francais correct » des titres IA.
 *
 * On PROPOSE, on n'impose pas : chaque correction se coche ou se decoche, et la
 * ligne montre l'avant et l'apres. Rien ne part sans un clic.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";

const KINDS = {
  accent: "accent manquant",
  ponctuation: "ponctuation",
  apostrophe: "apostrophe",
  espace: "espace",
  casse: "majuscules",
};

export const AiTitlesSentinelCard = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    setOpen(true);
    try {
      const { data: d } = await axios.get(`${API}/api/sentinel/ai-titles/scan`,
        { withCredentials: true, timeout: 120000 });
      setData(d);
      const tout = {};
      (d.candidates || []).forEach((c) => { tout[`${c.collection}:${c.id}`] = true; });
      setAccepted(tout);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Balayage impossible — réessayez.");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const cles = Object.keys(accepted).filter((k) => accepted[k]);

  const apply = async () => {
    const lot = (data?.candidates || []).filter((c) => accepted[`${c.collection}:${c.id}`]);
    if (!lot.length) return;
    setApplying(true);
    try {
      const { data: r } = await axios.post(`${API}/api/sentinel/ai-titles/apply`,
        { accepted: lot }, { withCredentials: true, timeout: 120000 });
      toast.success(`${r.applied} titre(s) corrigé(s).`);
      setOpen(false);
      setData(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Correction impossible.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="ai-titles-sentinel-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-zinc-900">Français des titres générés par IA</h3>
          <p className="text-xs text-zinc-600 mt-1 max-w-xl">
            Les titres sont désormais corrigés à la source (accents, ponctuation,
            apostrophes). Ce balayage relit ceux qui existent déjà et propose les
            corrections — rien n'est appliqué sans ton accord.
          </p>
        </div>
        <button
          onClick={scan}
          disabled={loading}
          className="flex-shrink-0 self-start px-3 py-2 rounded-lg bg-[#002FA7] hover:bg-blue-800 text-white text-xs font-bold disabled:opacity-50"
          data-testid="ai-titles-scan-btn"
        >
          {loading ? "Balayage…" : "Balayer les titres"}
        </button>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
             onClick={() => !applying && setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[88vh] flex flex-col shadow-2xl"
               onClick={(e) => e.stopPropagation()} data-testid="ai-titles-modal">
            <div className="px-5 py-4 border-b border-zinc-200">
              <h3 className="text-base font-bold text-zinc-900">Titres à corriger</h3>
              {data && (
                <p className="text-xs text-zinc-600 mt-1" data-testid="ai-titles-summary">
                  {data.candidates_total} titre(s) fautif(s) sur {data.scanned_total} analysé(s)
                  {Object.keys(data.by_kind || {}).length > 0 && " — "}
                  {Object.entries(data.by_kind || {})
                    .map(([k, n]) => `${n} ${KINDS[k] || k}`)
                    .join(", ")}
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
              {loading && <p className="text-sm text-zinc-500">Balayage en cours…</p>}
              {!loading && data && data.candidates_total === 0 && (
                <p className="text-sm text-teal-700 font-medium" data-testid="ai-titles-empty">
                  Aucun titre fautif — le français est correct partout.
                </p>
              )}
              {(data?.candidates || []).map((c) => {
                const k = `${c.collection}:${c.id}`;
                return (
                  <label key={k}
                         className="flex items-start gap-3 p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!accepted[k]}
                      onChange={(e) => setAccepted((p) => ({ ...p, [k]: e.target.checked }))}
                      data-testid={`ai-title-check-${c.id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-zinc-500">
                        {c.ku_type || c.source_label} · {(c.kinds || []).map((x) => KINDS[x] || x).join(", ")}
                      </div>
                      <div className="text-sm text-zinc-500 line-through break-words">{c.current}</div>
                      <div className="text-sm text-blue-800 font-medium break-words">{c.suggested}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="px-5 py-3 border-t border-zinc-200 flex flex-wrap items-center gap-2">
              <button onClick={() => {
                const tout = {};
                (data?.candidates || []).forEach((c) => { tout[`${c.collection}:${c.id}`] = true; });
                setAccepted(tout);
              }} className="px-3 py-2 rounded-lg border border-zinc-300 text-xs font-medium hover:bg-zinc-50"
                      data-testid="ai-titles-accept-all">
                Tout accepter
              </button>
              <button onClick={() => setAccepted({})}
                      className="px-3 py-2 rounded-lg border border-zinc-300 text-xs font-medium hover:bg-zinc-50"
                      data-testid="ai-titles-reject-all">
                Tout refuser
              </button>
              <span className="flex-1" />
              <button onClick={() => setOpen(false)} disabled={applying}
                      className="px-3 py-2 rounded-lg border border-zinc-300 text-xs font-medium hover:bg-zinc-50"
                      data-testid="ai-titles-cancel">
                Fermer sans corriger
              </button>
              <button onClick={apply} disabled={applying || cles.length === 0}
                      className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-blue-800 text-white text-xs font-bold disabled:opacity-40"
                      data-testid="ai-titles-apply">
                {applying ? "Correction…" : `Corriger la sélection (${cles.length})`}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default AiTitlesSentinelCard;
